import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TreeViewMeasuresConstantType,
    TreeViewDynamicStyleType,
    TreeViewMeasures,
    TreeViewSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getRowIconGap
} from "../../helpers/theme/control";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const TREE_VIEW_LABEL_VARIANTS: Record<TreeViewSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    small: "bodySmallSize",
    large: "bodyLargeSize"
};

export const TREE_VIEW_META_VARIANTS: Record<TreeViewSize, keyof NCoreUIKit.Typography> = {
    large: "bodyMediumSize",
    medium: "bodySmallSize",
    small: "bodySmallSize"
};

export const TREE_VIEW_SELECTED_VARIANT: keyof NCoreUIKit.Typography = "titleSmallSize";

export const getTreeViewMeasures = ({
    controlHeights,
    touchTargets,
    pointerType,
    iconSizes,
    radiuses,
    borders,
    variant,
    spaces,
    size
}: TreeViewMeasuresConstantType): TreeViewMeasures => {
    const isCoarse = pointerType === "coarse";

    const rowHeight = isCoarse ? touchTargets.preferred : controlHeights[size];

    const iconSize = iconSizes[size];

    const slotWidth = isCoarse ? touchTargets.coarse : iconSize;

    return {
        actionSize: isCoarse ? rowHeight : Math.min(touchTargets.fine, rowHeight - 2 * spaces.spacingXs),
        paddingStart: Math.max(0, spaces.spacingSm - (slotWidth - iconSize) / 2),
        toggleHit: Math.max(slotWidth, touchTargets.fine),
        labelVariant: TREE_VIEW_LABEL_VARIANTS[size],
        radius: variant === "outline" ? concentric({
            inset: spaces.spacingXs + borders.line,
            outer: radiuses.surface,
            isCapsuleExempt: false,
            role: radiuses.actions
        }) : radiuses.actions,
        metaVariant: TREE_VIEW_META_VARIANTS[size],
        indent: iconSize + spaces.spacingSm,
        paddingEnd: spaces.spacingSm,
        rowGap: spaces.spacingXxs,
        gap: getRowIconGap({
            controlHeights,
            rowHeight,
            spaces
        }),
        rowHeight,
        slotWidth,
        iconSize
    };
};

const stylesheet = StyleSheet.create({
    emptyIcon: {
        justifyContent: "center",
        alignItems: "center"
    },
    list: {
        flexDirection: "column",
        minWidth: 0
    },
    status: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden"
    },
    header: {
        alignItems: "center",
        flexDirection: "row"
    },
    empty: {
        alignItems: "center",
        alignSelf: "center"
    },
    emptyText: {
        textAlign: "center"
    },
    headerText: {
        minWidth: 0,
        flex: 1
    },
    container: {
        minWidth: 0
    }
});

export const useStyles = ({
    controlHeights,
    shapeConfigs,
    shadowAlpha,
    elevations,
    measures,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: TreeViewDynamicStyleType) => {
    const styles = {
        emptyIcon: {
            backgroundColor: colors.content.container.subtle,
            marginBottom: spaces.spacingSm,
            height: controlHeights.large,
            width: controlHeights.large,
            borderRadius: radiuses.full
        } as Mutable<ViewStyle>,
        empty: {
            maxWidth: shapeConfigs.dialogMaxWidth,
            paddingVertical: spaces.spacingLg,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        header: {
            paddingHorizontal: spaces.spacingSm,
            paddingBottom: spaces.spacingSm,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        container: {} as Mutable<ViewStyle>,
        emptyActions: {
            paddingTop: spaces.spacingMd
        } as Mutable<ViewStyle>,
        headerText: {
            gap: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        status: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>,
        list: {
            gap: measures.rowGap
        } as Mutable<ViewStyle>
    };

    if(variant === "outline") {
        Object.assign(styles.container, getSurfaceStyle({
            surface: "card",
            shadowAlpha,
            elevations,
            borders,
            colors
        }), getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }), {
            padding: spaces.spacingXs
        });
    }

    return styles;
};
export default stylesheet;
